import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { cloudTypes } from "@/components/weather/cloudTypesData";

const rootWords = [
  { prefix: "Cirro", meaning: "Wispy and high altitude", range: "above 18,000 ft" },
  { prefix: "Alto", meaning: "Mid-level", range: "6,500 to 18,000 ft" },
  { prefix: "Strato", meaning: "Flat, layered, or sheet-like", range: "often low to mid-level" },
  { prefix: "Cumulo", meaning: "Puffy, fluffy heaps", range: "can grow upward" },
  { prefix: "Nimbo", meaning: "Rain-bearing", range: "clouds that bring rain" },
];

const cloudGroups = [
  { title: "High-Level Clouds", names: ["Cirrus", "Cirrocumulus", "Cirrostratus"] },
  { title: "Mid-Level Clouds", names: ["Altostratus", "Altocumulus"] },
  { title: "Low-Level Clouds", names: ["Stratus", "Stratocumulus", "Nimbostratus"] },
  { title: "Vertical Clouds", names: ["Cumulus", "Cumulonimbus"] },
];

export default function CloudTypesGuide() {
  return (
    <section className="space-y-5" aria-label="Cloud type guide">
      <div className="rounded-[2rem] border border-white/70 bg-slate-950 p-5 text-white shadow-2xl shadow-ocean/20 sm:p-7">
        <p className="text-xs font-black uppercase tracking-[0.24em] !text-cyan-200">Cloud Type Guide</p>
        <h1 className="mt-2 text-3xl font-black tracking-tight !text-white sm:text-4xl">Understand clouds before conditions change</h1>
        <p className="mt-3 max-w-3xl text-sm leading-7 !text-cyan-50/90 sm:text-base">
          Use this guide to identify common cloud types and what they may mean for sailors, boaters, and coastal weather planning.
        </p>
      </div>

      <Card className="rounded-[1.5rem] border-white/60 bg-white/90 shadow-[0_14px_34px_rgba(15,23,42,0.08)]">
        <CardContent className="p-5">
          <h2 className="text-2xl font-black text-[#111827]">Cloud root words</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">Cloud names are easier to understand when you know the simple roots:</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {rootWords.map((item) => (
              <div key={item.prefix} className="rounded-2xl bg-cyan-50 p-4 ring-1 ring-cyan-100">
                <p className="text-lg font-black text-[#247C78]">{item.prefix}</p>
                <p className="mt-1 text-sm font-bold text-slate-800">{item.meaning}</p>
                <p className="mt-1 text-xs text-slate-500">{item.range}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-6">
        {cloudGroups.map((group) => {
          const groupClouds = group.names
            .map((name) => cloudTypes.find((cloud) => cloud.name === name))
            .filter(Boolean);

          return (
            <section key={group.title} className="space-y-3" aria-labelledby={`${group.title.toLowerCase().replaceAll(" ", "-")}-title`}>
              <h2 id={`${group.title.toLowerCase().replaceAll(" ", "-")}-title`} className="text-2xl font-black text-[#111827]">
                {group.title}
              </h2>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {groupClouds.map((cloud) => {
                  const Icon = cloud.icon;
                  return (
                    <Link key={cloud.name} to={`/weather/cloud-types/${cloud.slug}`} className="block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2">
                      <Card id={cloud.slug} className="h-full scroll-mt-24 rounded-[1.5rem] border-white/60 bg-white/90 shadow-[0_14px_34px_rgba(15,23,42,0.08)] transition hover:-translate-y-0.5 hover:bg-white">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#247C78]">{cloud.altitude}</p>
                              <h3 className="mt-2 text-2xl font-black text-[#111827]">{cloud.name}</h3>
                            </div>
                            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-cyan-50 text-[#247C78] ring-1 ring-cyan-100">
                              <Icon className="h-5 w-5" />
                            </div>
                          </div>
                          <div className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
                            <p><span className="font-black text-slate-900">Characteristics:</span> {cloud.traits}</p>
                            <p><span className="font-black text-slate-900">Marine meaning:</span> {cloud.marineMeaning}</p>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </section>
  );
}